import React from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useCMSContent } from '@/hooks/useCMSContent';
import { PageLoading } from './shared';

export const ContactPage = () => {
  const { content, loading } = useCMSContent();

  if (loading) return <PageLoading />;

  const footer = content?.footer || {};

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="contact-page">
      <SeoMeta
        title="Kontakta oss"
        description="Kontakta ByggFynd för frågor om order, säljkonto eller support. Hitta e-post, telefon och adress."
        canonicalPath="/kontakt"
      />
      <div className="container-main py-8">
        <Breadcrumbs items={[{ label: 'Kontakt' }]} />
        
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-black text-[#0F172A] mb-8">Kontakta oss</h1>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-md shadow-sm p-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-6">Skicka meddelande</h2>
              <form className="space-y-4">
                <div>
                  <Label htmlFor="name">Namn</Label>
                  <Input id="name" className="mt-1" data-testid="contact-name" />
                </div>
                <div>
                  <Label htmlFor="email">E-post</Label>
                  <Input id="email" type="email" className="mt-1" data-testid="contact-email" />
                </div>
                <div>
                  <Label htmlFor="subject">Ämne</Label>
                  <Input id="subject" className="mt-1" data-testid="contact-subject" />
                </div>
                <div>
                  <Label htmlFor="message">Meddelande</Label>
                  <Textarea id="message" rows={5} className="mt-1" data-testid="contact-message" />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#FF8A00] hover:bg-[#e67c00]"
                  data-testid="contact-submit"
                >
                  Skicka
                </Button>
              </form>
            </div>
            
            <div className="space-y-6">
              <div className="bg-white rounded-md shadow-sm p-8">
                <h2 className="text-xl font-bold text-[#0F172A] mb-6">Kontaktuppgifter</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Mail className="h-6 w-6 text-[#003B73] flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-[#0F172A]">E-post</p>
                      <a href={`mailto:${footer.contact_email || 'info@byggfynd.se'}`} className="text-gray-600 hover:text-[#003B73]">
                        {footer.contact_email || 'info@byggfynd.se'}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="h-6 w-6 text-[#003B73] flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-[#0F172A]">Telefon</p>
                      <a href={`tel:${footer.contact_phone || '08-123 456 78'}`} className="text-gray-600 hover:text-[#003B73]">
                        {footer.contact_phone || '08-123 456 78'}
                      </a>
                      <p className="text-sm text-gray-500">Vardagar 08-17</p>
                    </div>
                  </div>
                  {footer.contact_address && (
                    <div className="flex items-start gap-4">
                      <MapPin className="h-6 w-6 text-[#003B73] flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-[#0F172A]">Adress</p>
                        <p className="text-gray-600 whitespace-pre-line">{footer.contact_address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-[#003B73] rounded-md shadow-sm p-8 text-white">
                <h3 className="text-lg font-bold mb-2">Behöver du snabb hjälp?</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Kolla vår FAQ för svar på vanliga frågor.
                </p>
                <Link to="/faq">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003B73]">
                    Gå till FAQ
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
